/* eslint-disable react/no-unstable-nested-components */
import { useRef, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { debounce, kebabCase } from 'lodash';
import { KeyboardEventKey } from '$util/types';
import type { SearchSuggestionsProps, SuggestionMatchBase } from '$components/composite/SearchSuggestions';
import { SearchSuggestions } from '$components/composite/SearchSuggestions';

declare const ga: (event: 'send', type: 'pageview', url: string) => void | undefined;

const sendSearchTermAnalytics = debounce(
  (term: string, trigger: string) => {
    if (!term || typeof ga !== 'function') return;
    ga('send', 'pageview', `/virtual${window.location.pathname}?s=${term}&sc=trigger-${trigger}`);
  },
  1000,
  { leading: true }
);

export interface SearchSuggestionsAnalyticsProps<TSuggestion, TSection = unknown>
  extends SearchSuggestionsProps<TSuggestion, TSection> {
  /**
   * Send search terms to Google Analytics
   */
  readonly analytics?: boolean;
}

/**
 * This component extends the `SearchSuggestions` component with the ability
 * to send searched terms to google analytics.
 */
export function SearchSuggestionsAnalytics<TSuggestion extends SuggestionMatchBase, TSection = unknown>({
  analytics = false,
  getSuggestionValue,
  onSuggestionHighlighted,
  onSuggestionSelected,
  onFocus,
  onBlur,
  onClear,
  onKeyUp,
  className,
  ...props
}: SearchSuggestionsAnalyticsProps<TSuggestion, TSection>): JSX.Element {
  type Props = SearchSuggestionsProps<TSuggestion, TSection>;

  /**
   * Keep input status for analytics
   */
  const analyticsRef = useRef({ value: '', highlighted: false, focused: false });

  const handleKeyUp = useCallback<Required<Props>['onKeyUp']>(
    (e) => {
      if (analytics) {
        const key = e.key as KeyboardEventKey;
        const { value } = e.target as HTMLInputElement;
        if (![KeyboardEventKey.ArrowDown, KeyboardEventKey.ArrowUp, KeyboardEventKey.Enter].includes(key)) {
          /**
           * Keep note of what was typed so we know what to send to analytics when the input value
           * changes with a suggestion option.
           */
          analyticsRef.current.value = value;
          analyticsRef.current.highlighted = false;
        }
        if (
          [
            KeyboardEventKey.Backspace,
            KeyboardEventKey.Clear,
            KeyboardEventKey.Delete,
            KeyboardEventKey.Escape,
          ].includes(key)
        ) {
          sendSearchTermAnalytics(value, kebabCase(key));
        }
      }
      onKeyUp?.(e);
    },
    [analytics, onKeyUp]
  );

  const handleSuggestionHighlighted = useCallback<Required<Props>['onSuggestionHighlighted']>(
    (params) => {
      if (analytics && !analyticsRef.current.highlighted) {
        analyticsRef.current.highlighted = true;
        sendSearchTermAnalytics(analyticsRef.current.value, 'suggestion-highlight');
      }
      onSuggestionHighlighted?.(params);
    },
    [analytics, onSuggestionHighlighted]
  );

  const handleSuggestionSelected = useCallback<Required<Props>['onSuggestionSelected']>(
    (e, data) => {
      if (analytics) {
        analyticsRef.current.highlighted = true;
        sendSearchTermAnalytics(data.suggestionValue, 'suggestion-selected');
      }
      onSuggestionSelected?.(e, data);
    },
    [analytics, onSuggestionSelected]
  );

  const handleFocus = useCallback<Required<Props>['onFocus']>(
    (e) => {
      if (analytics) {
        analyticsRef.current.focused = true;
      }
      onFocus?.(e);
    },
    [analytics, onFocus]
  );

  const handleBlur = useCallback<Required<Props>['onBlur']>(
    (e, params) => {
      if (analytics) {
        analyticsRef.current.focused = false;
        sendSearchTermAnalytics(analyticsRef.current.value, 'focus-out');
      }
      onBlur?.(e, params);
    },
    [analytics, onBlur]
  );

  const handleClear = useCallback<Required<Props>['onClear']>(() => {
    if (analytics) {
      sendSearchTermAnalytics(analyticsRef.current.value, 'clear');
    }
    onClear?.();
  }, [analytics, onClear]);

  useEffect(() => {
    const sendAnalyticsAtPageLeave = () => {
      if (!analytics || !analyticsRef.current.focused) return;
      sendSearchTermAnalytics(analyticsRef.current.value, 'page-leave');
    };
    window.addEventListener('beforeunload', sendAnalyticsAtPageLeave);
    return () => window.removeEventListener('beforeunload', sendAnalyticsAtPageLeave);
  }, [analytics]);

  return (
    <SearchSuggestions<TSuggestion, TSection>
      className={classNames('SearchSuggestionsAnalytics', className)}
      getSuggestionValue={getSuggestionValue}
      onKeyUp={handleKeyUp}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClear={handleClear}
      onSuggestionHighlighted={handleSuggestionHighlighted}
      onSuggestionSelected={handleSuggestionSelected}
      {...props}
    />
  );
}
SearchSuggestionsAnalytics.displayName = 'SearchSuggestionsAnalytics';
